import { generateStatementPrintHtml, type GenerateStatementPrintHtmlOptions } from './statement-print-html';
import type { StatementPrintActivityRow, StatementPrintViewModel } from './statement-print.types';
import {
    generateChunkedPdf,
    STATEMENT_PDF_PAGE_FILL_MIN_RATIO,
} from './statement-chunk-pdf-generator';
import {
    PDF_OFFSCREEN_LEFT,
    STATEMENT_PRINT_CSS_PX_PER_IN,
    STATEMENT_PRINT_MARGIN_BOTTOM_IN,
    STATEMENT_PRINT_MARGIN_SIDE_IN,
    STATEMENT_PRINT_MARGIN_TOP_IN,
    STATEMENT_PRINT_PAGE_HEIGHT_IN,
    STATEMENT_PRINT_PAPER_WIDTH_PX,
} from './statement-pdf.theme';

const PAPER_WIDTH_PX = STATEMENT_PRINT_PAPER_WIDTH_PX;
/** Typical letter margin used when measuring remittance placement for PDF capture. */
const PRINT_PAGE_MARGIN_IN = 0.5;
/** If remittance starts with less than this many px left on the last page, force a new page. */
const REMIT_MIN_REMAINING_PX = 250;
/** Target height of each HTML→canvas capture (keeps html2canvas under browser canvas limits). */
const PAGES_PER_CHUNK = 15;

/**
 * Usable content height (px) for one letter portrait page,
 * matching paper padding used in statement print CSS.
 */
const usablePageHeightPx = (): number => {
    const contentHeightIn = STATEMENT_PRINT_PAGE_HEIGHT_IN - PRINT_PAGE_MARGIN_IN * 2;
    return contentHeightIn * STATEMENT_PRINT_CSS_PX_PER_IN;
};

/**
 * Full letter page height in CSS px. Matches the PDF slicer when page margins are 0
 * (content inset lives inside `.ps-paper` padding, not jsPDF margins).
 */
const pdfPageHeightPx = (): number =>
    STATEMENT_PRINT_PAGE_HEIGHT_IN * STATEMENT_PRINT_CSS_PX_PER_IN;

const mountOffscreenHost = (html: string, paperCss: string): HTMLElement => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const host = document.createElement('div');
    Object.assign(host.style, {
        position: 'fixed',
        left: PDF_OFFSCREEN_LEFT,
        top: '0',
        width: `${PAPER_WIDTH_PX}px`,
        visibility: 'hidden',
        pointerEvents: 'none',
        zIndex: '-1',
        overflow: 'visible',
    } as Partial<CSSStyleDeclaration>);

    const styleEl = doc.querySelector('style');
    if (styleEl) {
        host.appendChild(styleEl.cloneNode(true));
    }
    const measureOverrides = document.createElement('style');
    measureOverrides.textContent = paperCss;
    host.appendChild(measureOverrides);

    const paper = doc.querySelector('.ps-paper');
    if (paper) {
        host.appendChild(paper.cloneNode(true));
    } else {
        Array.from(doc.body.childNodes).forEach(node => {
            host.appendChild(node.cloneNode(true));
        });
    }

    document.body.appendChild(host);
    return host;
};

/**
 * If the remittance slip would start with little room remaining on its page,
 * insert `<div class="page-break"></div>` so the slip begins on a new page.
 */
export const insertRemittancePageBreakIfNeeded = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const remit = doc.querySelector('.ps-remit') as HTMLElement | null;
    if (!remit) {
        return html;
    }
    if (remit.previousElementSibling?.classList.contains('page-break')) {
        return html;
    }

    const host = mountOffscreenHost(
        html,
        `
      .ps-paper {
        width: ${PAPER_WIDTH_PX}px !important;
        max-width: ${PAPER_WIDTH_PX}px !important;
        margin: 0 !important;
        padding: 0.4in 0.5in !important;
        box-sizing: border-box !important;
      }
    `,
    );

    try {
        const measurePaper = (host.querySelector('.ps-paper') as HTMLElement | null) || host;
        const measureRemit = host.querySelector('.ps-remit') as HTMLElement | null;
        if (!measureRemit) {
            return html;
        }

        const pageH = usablePageHeightPx();
        const offsetInPaper = measureRemit.offsetTop - measurePaper.offsetTop;
        const positionOnPage = ((offsetInPaper % pageH) + pageH) % pageH;
        const remaining = pageH - positionOnPage;

        if (remaining >= REMIT_MIN_REMAINING_PX) {
            return html;
        }

        const breakEl = doc.createElement('div');
        breakEl.className = 'page-break';
        remit.parentNode?.insertBefore(breakEl, remit);

        return `<!doctype html>\n${doc.documentElement.outerHTML}`;
    } finally {
        host.remove();
    }
};

export const buildStatementPrintHtml = (
    model: StatementPrintViewModel,
    options?: GenerateStatementPrintHtmlOptions,
): string => {
    const raw = generateStatementPrintHtml(model, options);
    return insertRemittancePageBreakIfNeeded(raw);
};

/** Bottom edge of each `.ps-grid-row`, in DOM px relative to the measure host. */
const measureActivityRowBottomsPx = (
    model: StatementPrintViewModel,
    rows: StatementPrintActivityRow[],
): number[] => {
    if (!rows.length) {
        return [];
    }

    const html = buildStatementPrintHtml(model, {
        showHeader: true,
        showFooter: true,
        activityRows: rows,
    });
    const host = mountOffscreenHost(
        html,
        `
      .ps-paper {
        width: ${PAPER_WIDTH_PX}px !important;
        max-width: ${PAPER_WIDTH_PX}px !important;
        margin: 0 !important;
        overflow-x: hidden !important;
        overflow-y: visible !important;
        box-sizing: border-box !important;
      }
    `,
    );

    try {
        const hostTop = host.getBoundingClientRect().top;
        const rowEls = Array.from(host.querySelectorAll('.ps-grid-row')) as HTMLElement[];
        return rowEls.map(el => el.getBoundingClientRect().bottom - hostTop);
    } finally {
        host.remove();
    }
};

/**
 * Mirror PDF slicer page cuts in DOM px so chunk boundaries match real pages
 * (row-snap underfills; ideal `pages*pageHeight` overshoots by ~1 page).
 */
const simulateSlicerPageEndsPx = (
    rowBottomsPx: number[],
    contentHeightPx: number,
    pageHeightPx: number,
): number[] => {
    const bleedPx = 2;
    const minSlicePx = Math.floor(pageHeightPx * STATEMENT_PDF_PAGE_FILL_MIN_RATIO);
    const skipableTailPx = 4;
    const pageEnds: number[] = [];
    let startY = 0;

    while (startY < contentHeightPx) {
        const idealEnd = Math.min(contentHeightPx, startY + pageHeightPx);
        const remainingAfterIdeal = contentHeightPx - idealEnd;
        const isLastSlice = remainingAfterIdeal <= skipableTailPx;

        if (isLastSlice) {
            if (contentHeightPx - startY > skipableTailPx) {
                pageEnds.push(contentHeightPx);
            }
            break;
        }

        const safeEndLimit = Math.max(startY + 1, idealEnd - bleedPx);
        let cutY = 0;
        for (let i = rowBottomsPx.length - 1; i >= 0; i--) {
            const y = rowBottomsPx[i];
            if (y <= safeEndLimit && y > startY + minSlicePx) {
                cutY = y;
                break;
            }
        }
        if (!cutY) {
            cutY = idealEnd;
        }
        if (cutY <= startY) {
            cutY = Math.min(contentHeightPx, startY + pageHeightPx);
        }

        pageEnds.push(cutY);
        startY = cutY;
    }

    return pageEnds;
};

const lastRowIndexAtOrBeforeY = (rowBottomsPx: number[], y: number, fromIdx: number): number => {
    let endIdx = fromIdx;
    for (let i = fromIdx; i < rowBottomsPx.length; i++) {
        if (rowBottomsPx[i] <= y) {
            endIdx = i + 1;
        } else {
            break;
        }
    }
    return endIdx <= fromIdx ? fromIdx + 1 : endIdx;
};

/**
 * Split activity rows into chunks of ~`pagesPerChunk` pages using the same
 * row-snap model as PDF slicing.
 */
const chunkActivityRowsAtPageBoundaries = (
    model: StatementPrintViewModel,
    rows: StatementPrintActivityRow[],
    pagesPerChunk: number,
    pageHeightPx: number,
): StatementPrintActivityRow[][] => {
    if (!rows.length) {
        return [[]];
    }

    const rowBottomsPx = measureActivityRowBottomsPx(model, rows);
    if (rowBottomsPx.length !== rows.length) {
        return [rows];
    }

    const contentHeightPx = rowBottomsPx[rowBottomsPx.length - 1] ?? 0;
    const pageEnds = simulateSlicerPageEndsPx(rowBottomsPx, contentHeightPx, pageHeightPx);

    if (pageEnds.length <= pagesPerChunk) {
        return [rows];
    }

    const cutYs: number[] = [];
    for (let i = pagesPerChunk - 1; i < pageEnds.length - 1; i += pagesPerChunk) {
        cutYs.push(pageEnds[i]);
    }

    if (!cutYs.length) {
        return [rows];
    }

    const chunks: StatementPrintActivityRow[][] = [];
    let startIdx = 0;
    for (const cutY of cutYs) {
        const endIdx = Math.min(lastRowIndexAtOrBeforeY(rowBottomsPx, cutY, startIdx), rows.length);
        chunks.push(rows.slice(startIdx, endIdx));
        startIdx = endIdx;
        if (startIdx >= rows.length) {
            break;
        }
    }
    if (startIdx < rows.length) {
        chunks.push(rows.slice(startIdx));
    }

    return chunks.length ? chunks : [rows];
};

/** Build HTML and download a portrait PDF of the patient statement. */
export const downloadStatementPdf = async (model: StatementPrintViewModel): Promise<void> => {
    const filename = `${model.patientName} - ${model.statementDate}.pdf`;
    const activityRows = model.activityRows || [];
    const rowChunks = chunkActivityRowsAtPageBoundaries(
        model,
        activityRows,
        PAGES_PER_CHUNK,
        pdfPageHeightPx(),
    );

    const htmlChunks = rowChunks.map((rows, chunkIndex) =>
        buildStatementPrintHtml(model, {
            showHeader: chunkIndex === 0,
            showFooter: chunkIndex === rowChunks.length - 1,
            activityRows: rows,
        }),
    );

    await generateChunkedPdf({
        htmlChunks,
        filename,
        jsPdf: {
            unit: 'in',
            format: 'letter',
            orientation: 'portrait',
            margin: [
                STATEMENT_PRINT_MARGIN_TOP_IN,
                STATEMENT_PRINT_MARGIN_SIDE_IN,
                STATEMENT_PRINT_MARGIN_BOTTOM_IN,
                STATEMENT_PRINT_MARGIN_SIDE_IN,
            ],
        },
        html2canvas: {
            canvasScale: 1.5,
            backgroundColor: '#ffffff',
            useCORS: true,
        },
    });
};
